import { storage } from '@/utils/storage'

// Umami payload key 標準化：xlsx 規格 payload 全為 snake_case；
// 在 plugin 層統一轉換，所有 caller（$analytics.push 分派 / 直接 $umami.track）都自動合規。
const camelToSnake = (s) => s.replace(/[A-Z]/g, (m, i) => (i ? '_' : '') + m.toLowerCase())
const toSnakeKeys = (obj) => {
  if (!obj || typeof obj !== 'object') return obj
  const out = {}
  for (const k in obj) out[camelToSnake(k)] = obj[k]
  return out
}

// UTM 持久化（last-touch attribution）
// 動機：Umami 的 Sources/Channels 報表是 session 級歸因，依賴第一個 pageview 的 url query string。
// PWA 從桌面啟動 / FB-IG-LINE WebView / 直接輸入網址 的 session 因為沒有 document.referrer 且
// URL 無 UTM，全部歸到 "(無)"，導致 Revenue → Sources 看不到真實投放成效。
// 做法：URL 一旦帶 utm_* 就存進 localStorage（最近一次覆蓋舊值），之後每次 pageview 若 URL
// 沒帶 utm_* 就用 stored 值補回 url query，讓 Umami server 端能正確歸因每個新 session。
// 30 天 TTL 對齊主流廣告平台 click-through window（FB 7/28d、Google 30/90d）的中位數。
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id']
const UTM_TTL_MS = 30 * 24 * 60 * 60 * 1000
// 敏感 query key 白名單（pageview URL 補回時剝除）：避免 token / OTP / 手機號等
// 經由 Umami URL 與 stored UTM 一起被 server 收集
const SENSITIVE_QUERY_KEYS = new Set(['token', 'otp', 'phone', 'code', 'password', 'auth', 'access_token'])

export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig()
  const { umamiWebsiteId, umamiScriptUrl, umamiDomains, umamiDebug, clientId } = config.public || {}
  const debug = umamiDebug === '1' || umamiDebug === 1 || umamiDebug === true

  if (!umamiWebsiteId || !umamiScriptUrl) {
    return {
      provide: {
        umami: { track: () => {}, enabled: false },
      },
    }
  }

  // localStorage key 以 clientId 作 namespace，避免同瀏覽器跨品牌 PWA 互相覆蓋 UTM
  const UTM_STORAGE_KEY = `__umami_utm_${clientId || 'default'}`

  const readUtmStore = () => {
    try {
      const raw = localStorage.getItem(UTM_STORAGE_KEY)
      if (!raw) return null
      const parsed = JSON.parse(raw)
      if (!parsed || !parsed.utm || !parsed.ts) return null
      if (Date.now() - parsed.ts > UTM_TTL_MS) {
        localStorage.removeItem(UTM_STORAGE_KEY)
        return null
      }
      return parsed.utm
    } catch (e) {
      // 隱私模式 / quota 滿 / storage 已停用情境下靜默失效，但保留 dev 觀察點
      if (debug) console.warn('[umami] readUtmStore failed:', e?.message || e)
      return null
    }
  }

  const captureUtm = (search) => {
    const params = new URLSearchParams(search || '')
    const utm = {}
    for (const key of UTM_KEYS) {
      const v = params.get(key)
      if (v) utm[key] = v
    }
    if (Object.keys(utm).length === 0) return
    try {
      localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify({ utm, ts: Date.now() }))
    } catch (e) {
      if (debug) console.warn('[umami] captureUtm failed:', e?.message || e)
    }
  }

  const buildAttributedUrl = (path, search) => {
    const original = new URLSearchParams(search || '')
    // 先剝敏感 query：避免 token / OTP / phone 被送進 Umami URL
    for (const key of [...original.keys()]) {
      if (SENSITIVE_QUERY_KEYS.has(key.toLowerCase())) original.delete(key)
    }
    // 現在的 URL 自己有 utm_* 就尊重之，不覆蓋（last-touch 已在 captureUtm 時更新）
    if (UTM_KEYS.some((k) => original.has(k))) {
      const qs = original.toString()
      return qs ? `${path}?${qs}` : path
    }
    const stored = readUtmStore()
    if (stored) {
      for (const [k, v] of Object.entries(stored)) original.set(k, v)
    }
    const qs = original.toString()
    return qs ? `${path}?${qs}` : path
  }

  // 初次載入先攔截 landing URL 的 utm_*，確保即使第一個 pageview 在 router.afterEach 之前發生，
  // 後續 session（含 Umami 因 inactivity timeout 切出的新 session）也能拿到 UTM 歸因。
  captureUtm(window.location.search)

  if (!window.__umamiQueue) window.__umamiQueue = []

  // Umami v2：track(string, obj) 帶 name → custom event；track(fn) 回傳不含 name → pageview。
  // 用 kind 區分兩條路徑，pageview 一律走 fn 形式，避免被當自訂事件記到 Events tab。
  // track() 回傳 Promise，需 attach .catch 吸收 async reject（例如 server 回 "Event limit reached"、
  // 429、CORS 等），否則會冒泡為 unhandled promise rejection 污染 console / Sentry。
  const sendToUmami = (kind, event, payload) => {
    const ret = kind === 'pageview' ? window.umami.track((props) => ({ ...props, ...(payload || {}) })) : window.umami.track(event, payload)
    if (ret && typeof ret.then === 'function') {
      ret.catch((err) => {
        if (debug) console.warn('[umami] send rejected', err)
      })
    }
  }

  const flushQueue = () => {
    if (!window.umami || typeof window.umami.track !== 'function') return
    const queue = window.__umamiQueue.splice(0)
    queue.forEach(([kind, event, payload]) => {
      try {
        sendToUmami(kind, event, payload)
      } catch (e) {
        if (debug) console.warn('[umami] flush error', e)
      }
    })
  }

  let isLoaded = false
  const initUmami = () => {
    if (isLoaded) return
    isLoaded = true
    const script = document.createElement('script')
    script.defer = true
    script.src = umamiScriptUrl
    script.setAttribute('data-website-id', umamiWebsiteId)
    script.setAttribute('data-auto-track', 'false')
    // 不設 data-exclude-search：Umami 後端 parser 完全依賴 url 字串中的 query 來歸類 UTM，
    // 排除後 paid 流量會被誤判為 organic。手動 pageview payload 帶完整 query 之前，
    // buildAttributedUrl 會剝掉 SENSITIVE_QUERY_KEYS（token/OTP/phone 等）。
    script.setAttribute('data-exclude-hash', 'true')
    if (umamiDomains) script.setAttribute('data-domains', umamiDomains)
    // 先 identify 再 flush：避免 script 載入前排隊的 pageview/event 在 identity 設定前送出，缺少 uid_site
    script.onload = () => {
      runIdentify()
      flushQueue()
    }
    document.head.appendChild(script)

    // removeEventListener options 只看 capture，passive 會被忽略；省略以避免 reviewer 誤解
    events.forEach((event) => window.removeEventListener(event, initUmami))
  }

  const events = ['scroll', 'mousemove', 'touchstart', 'keydown', 'click']
  events.forEach((event) => {
    window.addEventListener(event, initUmami, { passive: true, once: true })
  })

  setTimeout(initUmami, 5000)

  const dispatch = (kind, event, payload) => {
    const normalized = toSnakeKeys(payload)
    if (debug) console.info('[umami]', kind, event, normalized)
    if (window.umami && typeof window.umami.track === 'function') {
      try {
        sendToUmami(kind, event, normalized)
      } catch (e) {
        if (debug) console.warn('[umami] track error', e)
      }
    } else {
      window.__umamiQueue.push([kind, event, normalized])
    }
  }

  const track = (event, payload) => {
    if (!event) return
    dispatch('event', event, payload)
  }

  const trackPageview = (payload) => dispatch('pageview', null, payload)

  // === Player identify（TI-15419）===
  // 兩段式 identify：未登入只送 UTM 屬性（identify(data)）；登入後改送 identify(uid, data)，
  // 讓 uid_site 成為 Umami session 的「唯一識別碼」，後台才能用 UID 搜到玩家、並在「屬性」看到來源 UTM。
  // payload key 已是 snake_case，直接送、不需 toSnakeKeys。
  const { uidSite, userInfo } = useUserInfo()
  let lastIdentifySig = null
  let lastUid = null

  const buildUtmData = () => {
    // 只帶「實際有值」的欄位：readUtmStore 本身只存有值的 utm_*；避免空字串灌進 Umami「屬性」頁，
    // 也避免 identify 的逐-key merge 用空字串覆蓋 session 內先前設過的真實值。
    const data = { ...(readUtmStore() || {}) }
    if (uidSite.value) {
      // 登入分支：一律以 profile.agent_id 覆蓋（含空字串），不 fallback 舊 ac。
      // 這裡不能用「沒有值就省略 key」——umami session data 是逐 key merge，省略會讓訪客期間
      // 寫入的舊 ac 值繼續留在已登入 UID 的 session 上，所以 agent_id 為空時要主動送空字串清掉。
      data.agent_id = userInfo.value?.agent_id || ''
    } else {
      // 未登入才 fallback 落地時 00.query 存下的 ac（storage 7 天 TTL）；沒有 ac 就不帶這個 key
      const agentId = storage.get('ac')
      if (agentId) data.agent_id = agentId
    }
    return data
  }

  const runIdentify = () => {
    // identify 只在 tracker script 載入後存在；未就緒先跳過，後續 route 變更 / onload 會再觸發
    if (!window.umami || typeof window.umami.identify !== 'function') return
    const uid = uidSite.value || ''
    const utmData = buildUtmData()
    // 登出（uid 從有變無）：umami tracker 只在收到 string id 時才更新內部 identity，
    // 若這裡直接 return，殘留的舊玩家 identity 會繼續污染登出後、下一位登入前的事件，
    // 所以即使沒有 UTM/ac 資料也要送一次 identify('') 明確清空
    const isLogout = !uid && !!lastUid
    // 訪客且完全沒有 UTM / ac → 無可歸因資訊，跳過避免送空 session 屬性
    if (!uid && !isLogout && Object.keys(utmData).length === 0) return
    const sig = JSON.stringify({ uid, utmData })
    // 去重：同樣的 uid + utmData 不重送（換頁時 afterEach 會反覆呼叫）
    if (sig === lastIdentifySig) return
    lastIdentifySig = sig
    lastUid = uid
    try {
      // uid 為空字串時 identify('', utmData) 仍是合法 string id，會清空 tracker 內殘留的舊 identity
      const ret = window.umami.identify(uid, utmData)
      if (ret && typeof ret.then === 'function') ret.catch((err) => debug && console.warn('[umami] identify rejected', err))
    } catch (e) {
      if (debug) console.warn('[umami] identify error', e)
    }
  }

  // 登入 / profile 載入後 uid_site 才出現；route 沒變的情境 afterEach 抓不到，靠此 watch 補送一次
  watch(uidSite, () => runIdentify())

  // Umami-only 頁面層事件：每次 route 變更時送 open_page + page_view，不經 $analytics（避免 modal push 污染）
  // payload 對齊 xlsx 規格：location = pathname、page_title = document.title
  const router = useRouter()
  if (router) {
    router.afterEach(() => {
      setTimeout(() => {
        const path = window.location.pathname
        const search = window.location.search
        const page_title = document.title
        // 每次導航都嘗試攔截新出現的 UTM（例如 deep link 開啟、廣告中途切換 campaign）
        captureUtm(search)
        track('open_page', { location: path, page_title, window_type: 'tab', action_source: 'navigation' })
        // pageview 走函式形式（不帶 name），讓 Umami 計入 Views/Sessions
        // url 帶 search 並補回 stored utm_*，Umami 後端 parser 才能從 query 抽出 UTM 歸類 paid 流量
        trackPageview({ url: buildAttributedUrl(path, search), title: page_title })
        // 每次導航後依當前登入狀態 / UTM 補送 identify（去重後同值不重送）
        runIdentify()
      }, 0)
    })
  }

  return {
    provide: {
      umami: { track, enabled: true },
    },
  }
})
